/**
 * Shared frontend auth helpers — small fetch-based wrappers around the BA
 * endpoints we don't already get for free from `useAuth()` in
 * `@flowlib/user-auth/ui`.
 *
 * Why these live here (not in the upstream auth plugin):
 *   - The upstream `AuthProvider` only exposes email/password sign-in, sign-
 *     up, and 2FA. Hosted needs Google OAuth, email verification status, and
 *     organization-plugin endpoints.
 *   - Keeping these as plain `fetch` calls avoids pulling the better-auth
 *     client SDK into the browser bundle.
 */

/** Build the auth API base URL from the AuthProvider's apiBaseUrl. */
export function buildAuthApiBase(apiBaseUrl: string): string {
  return `${apiBaseUrl}/plugins/auth/api/auth`;
}

// ─────────────────────────────────────────────────────────────
// Social sign-in
// ─────────────────────────────────────────────────────────────

export interface SocialSignInOptions {
  provider: 'google' | 'github';
  /** Where to land after the OAuth round-trip. Defaults to current origin root. */
  callbackURL?: string;
  /** Where to send the user if OAuth fails. */
  errorCallbackURL?: string;
}

/**
 * Start a social sign-in flow. POSTs to `/sign-in/social`, expects a JSON
 * `{ url }` response with the provider's authorization URL, then sets
 * `window.location` to it (full page navigation — OAuth requires a real
 * top-level redirect, not a fetch).
 */
export async function startSocialSignIn(
  authApiBase: string,
  opts: SocialSignInOptions,
): Promise<void> {
  const callbackURL = opts.callbackURL ?? window.location.origin;
  const res = await fetch(`${authApiBase}/sign-in/social`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      provider: opts.provider,
      callbackURL,
      ...(opts.errorCallbackURL ? { errorCallbackURL: opts.errorCallbackURL } : {}),
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Social sign-in failed (${res.status}): ${body}`);
  }
  const data = (await res.json().catch(() => null)) as { url?: string; redirect?: boolean } | null;
  if (!data?.url) {
    throw new Error('Social sign-in did not return a redirect URL');
  }
  window.location.assign(data.url);
}

// ─────────────────────────────────────────────────────────────
// Magic link sign-in / sign-up
// ─────────────────────────────────────────────────────────────

/**
 * Request a passwordless sign-in email. Routes through BA's magicLink
 * plugin (`POST /sign-in/magic-link`). When the email is unknown the plugin
 * auto-registers the user; we seed `name` with the email local-part to
 * satisfy our `user.name NOT NULL` schema (the onboarding wizard overwrites
 * it via /update-user once the user picks a real name).
 */
export async function sendMagicLink(authApiBase: string, email: string): Promise<void> {
  const trimmed = email.trim();
  const res = await fetch(`${authApiBase}/sign-in/magic-link`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email: trimmed,
      name: trimmed.split('@')[0] || trimmed,
      callbackURL: window.location.origin,
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Magic link send failed (${res.status}): ${body}`);
  }
}

// ─────────────────────────────────────────────────────────────
// Email verification
// ─────────────────────────────────────────────────────────────

/** Resend the email verification link to a (presumed-existing) user. */
export async function resendVerificationEmail(authApiBase: string, email: string): Promise<void> {
  const res = await fetch(`${authApiBase}/send-verification-email`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email,
      callbackURL: window.location.origin,
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Resend failed (${res.status}): ${body}`);
  }
}

// ─────────────────────────────────────────────────────────────
// Organization plugin endpoints
// ─────────────────────────────────────────────────────────────

export interface BAOrganization {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
  createdAt?: string;
}

export async function listOrganizations(authApiBase: string): Promise<BAOrganization[]> {
  const res = await fetch(`${authApiBase}/organization/list`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`organization/list failed (${res.status})`);
  }
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? (data as BAOrganization[]) : [];
}

export async function checkOrganizationSlug(authApiBase: string, slug: string): Promise<boolean> {
  const res = await fetch(`${authApiBase}/organization/check-slug`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ slug }),
  });
  if (!res.ok) {
    // BA returns 400 with `{ status: false }` for taken slugs and 200 for
    // available ones. Treat any non-2xx as "taken" so the caller can
    // surface a friendly error.
    return false;
  }
  const data = (await res.json().catch(() => null)) as { status?: boolean } | null;
  return data?.status === true;
}

export async function createOrganization(
  authApiBase: string,
  input: { name: string; slug: string },
): Promise<BAOrganization> {
  const res = await fetch(`${authApiBase}/organization/create`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      name: input.name,
      slug: input.slug,
      // keepCurrentActiveOrganization defaults to false → BA flips the
      // session's activeOrganizationId to the new org for us, which is
      // exactly what we want for first-time onboarding.
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`organization/create failed (${res.status}) ${body}`);
  }
  return (await res.json()) as BAOrganization;
}

export async function setActiveOrganization(
  authApiBase: string,
  organizationId: string,
): Promise<void> {
  const res = await fetch(`${authApiBase}/organization/set-active`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ organizationId }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`organization/set-active failed (${res.status}) ${body}`);
  }
}

// ─────────────────────────────────────────────────────────────
// User profile (name update)
// ─────────────────────────────────────────────────────────────

/**
 * Update the current user's display name via BA's `/update-user` endpoint.
 * Used by the onboarding wizard when the user signed up with email and
 * didn't supply a name (Google OAuth populates this for us).
 */
export async function updateUserName(authApiBase: string, name: string): Promise<void> {
  const res = await fetch(`${authApiBase}/update-user`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`update-user failed (${res.status}) ${body}`);
  }
}

// ─────────────────────────────────────────────────────────────
// Org members + invitations (org-scoped user management)
//
// All endpoints below operate on the user's *active* organization unless
// `organizationId` is passed in. They mirror Better Auth's organization
// plugin endpoints — see the BA dist/plugins/organization/routes/ files
// for response shapes.
// ─────────────────────────────────────────────────────────────

/** A row in /organization/list-members — BA enriches with `user`. */
export interface BAOrgMember {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  createdAt: string;
  user?: {
    id: string;
    name?: string | null;
    email: string;
    image?: string | null;
  };
}

export interface BAOrgInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  organizationId: string;
  inviterId: string;
  expiresAt: string;
  teamId?: string | null;
}

/** Member role on the active org (`'owner' | 'admin' | 'member' | null`). */
export async function getActiveMemberRole(authApiBase: string): Promise<string | null> {
  // BA returns the active member object (or null when the user has no active org).
  const res = await fetch(`${authApiBase}/organization/get-active-member`, {
    credentials: 'include',
  });
  if (!res.ok) {
    return null;
  }
  const data = (await res.json().catch(() => null)) as { role?: string } | null;
  return data?.role ?? null;
}

/** List members of the current active org. */
export async function listOrgMembers(authApiBase: string): Promise<BAOrgMember[]> {
  const res = await fetch(`${authApiBase}/organization/list-members`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`organization/list-members failed (${res.status})`);
  }
  const data = (await res.json().catch(() => null)) as
    | { members?: BAOrgMember[]; total?: number }
    | BAOrgMember[]
    | null;
  if (Array.isArray(data)) {
    return data;
  }
  return data?.members ?? [];
}

export async function listOrgInvitations(authApiBase: string): Promise<BAOrgInvitation[]> {
  const res = await fetch(`${authApiBase}/organization/list-invitations`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`organization/list-invitations failed (${res.status})`);
  }
  const data = (await res.json().catch(() => [])) as BAOrgInvitation[];
  return Array.isArray(data) ? data : [];
}

export async function inviteOrgMember(
  authApiBase: string,
  input: { email: string; role: 'member' | 'admin'; resend?: boolean },
): Promise<BAOrgInvitation> {
  const res = await fetch(`${authApiBase}/organization/invite-member`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`organization/invite-member failed (${res.status}) ${body}`);
  }
  return (await res.json()) as BAOrgInvitation;
}

export async function updateOrgMemberRole(
  authApiBase: string,
  input: { memberId: string; role: 'member' | 'admin' | 'owner' },
): Promise<void> {
  const res = await fetch(`${authApiBase}/organization/update-member-role`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`organization/update-member-role failed (${res.status}) ${body}`);
  }
}

export async function removeOrgMember(authApiBase: string, memberIdOrEmail: string): Promise<void> {
  const res = await fetch(`${authApiBase}/organization/remove-member`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ memberIdOrEmail }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`organization/remove-member failed (${res.status}) ${body}`);
  }
}

export async function cancelOrgInvitation(
  authApiBase: string,
  invitationId: string,
): Promise<void> {
  const res = await fetch(`${authApiBase}/organization/cancel-invitation`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ invitationId }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`organization/cancel-invitation failed (${res.status}) ${body}`);
  }
}

// ─────────────────────────────────────────────────────────────
// Org id (slug) validation
// ─────────────────────────────────────────────────────────────

export const ORG_ID_MAX_LENGTH = 26;
const ORG_ID_PATTERN = /^[a-z0-9]+$/;

/**
 * Validate an org id against the hosted rules:
 *   - lowercase alphanumeric only (url-safe, no separators)
 *   - 2..26 chars
 *
 * Returns null on success, an error message string on failure.
 */
export function validateOrgId(input: string): string | null {
  if (!input) {
    return 'Workspace id is required';
  }
  if (input.length < 2) {
    return 'Workspace id must be at least 2 characters';
  }
  if (input.length > ORG_ID_MAX_LENGTH) {
    return `Workspace id must be at most ${ORG_ID_MAX_LENGTH} characters`;
  }
  if (!ORG_ID_PATTERN.test(input)) {
    return 'Use lowercase letters and numbers only — no spaces, dashes, or symbols.';
  }
  return null;
}

/** Normalize freeform input into a candidate org id (strips non-alphanumerics). */
export function suggestOrgId(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, ORG_ID_MAX_LENGTH);
}
